import { createInertiaApp, router } from "@inertiajs/react"
import { StrictMode } from "react"
import { createRoot } from "react-dom/client"

import { InertiaToaster } from "@/components/inertia-toaster"
import { capturePostHogPageview, setupPostHog } from "@/lib/posthog"

const appName = typeof document === "undefined"
  ? ""
  : document.querySelector('meta[name="application-name"]')?.getAttribute("content") || ""

createInertiaApp({
  title: () => appName,

  // Disable progress bar
  //
  // see https://inertia-rails.dev/guide/progress-indicators
  // progress: false,

  resolve: (name) => {
    const pages = import.meta.glob('../pages/**/*.jsx', {
      eager: true,
    })
    const page = pages[`../pages/${name}.jsx`]
    if (!page) {
      console.error(`Missing Inertia page component: '${name}.jsx'`)
    }

    // To use a default layout, import the Layout component
    // and use the following lines.
    // see https://inertia-rails.dev/guide/pages#default-layouts
    //
    page.default.layout ||= (page) => <InertiaToaster>{page}</InertiaToaster>

    return page
  },

  setup({ el, App, props }) {
    if (typeof window !== "undefined") {
      setupPostHog(props.initialPage.props)
      capturePostHogPageview()

      router.on("navigate", (event) => {
        setupPostHog(event.detail.page.props)
        capturePostHogPageview()
      })
    }

    createRoot(el).render(
      <StrictMode>
        <App {...props} />
      </StrictMode>
    )
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
      withAllErrors: true,
    },
    future: {
      useScriptElementForInitialPage: true,
      useDataInertiaHeadAttribute: true,
      useDialogForErrorModal: true,
      preserveEqualProps: true,
    },
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById("app")) {
    throw error
  } else {
    console.error(
      "Missing root element.\n\n" +
      "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
      'Consider moving <%= vite_javascript_tag "inertia.jsx" %> to the Inertia-specific layout instead.'
    )
  }
})
